/**
 * EvvSummaryWidget — Electronic Visit Verification compliance summary
 *
 * 21st Century Cures Act requires EVV for personal care services.
 * Shows GPS verification rate, missing punches, and compliance %.
 */

import React from 'react';
import {
  Card,
  Group,
  Progress,
  RingProgress,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Tooltip,
} from '@mantine/core';
import {
  IconMapPin,
  IconMapPinOff,
  IconAlertTriangle,
  IconCheck,
} from '@tabler/icons-react';
import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';

const GET_EVV_SUMMARY = gql`
  query GetEvvSummary($startDate: String, $endDate: String) {
    payrollPunchRecords(limit: 500) {
      id
      punchType
      latitude
      longitude
      accuracy
      status
    }
  }
`;

interface PunchRecord {
  id: string;
  punchType: string;
  latitude: number | null;
  longitude: number | null;
  accuracy: number | null;
  status: string;
}

interface EvvSummaryWidgetProps {
  title?: string;
  compact?: boolean;
}

export function EvvSummaryWidget({ title = 'EVV Compliance', compact = false }: EvvSummaryWidgetProps) {
  const { data, loading } = useQuery<{ payrollPunchRecords: PunchRecord[] }>(GET_EVV_SUMMARY);

  const punches = data?.payrollPunchRecords ?? [];
  const totalPunches = punches.length;
  const withGps = punches.filter(p => p.latitude !== null && p.longitude !== null).length;
  const withoutGps = totalPunches - withGps;
  const compliancePercent = totalPunches > 0 ? Math.round((withGps / totalPunches) * 100) : 100;

  // Accuracy breakdown
  const highAccuracy = punches.filter(p => p.accuracy !== null && p.accuracy <= 10).length;
  const mediumAccuracy = punches.filter(p => p.accuracy !== null && p.accuracy > 10 && p.accuracy <= 50).length;
  const lowAccuracy = punches.filter(p => p.accuracy !== null && p.accuracy > 50).length;

  if (loading) {
    return (
      <Card withBorder padding="lg">
        <Text size="sm" c="dimmed">Loading EVV data...</Text>
      </Card>
    );
  }

  if (compact) {
    return (
      <Card withBorder padding="md">
        <Group justify="space-between">
          <Stack gap={0}>
            <Text size="sm" c="dimmed">{title}</Text>
            <Text size="xl" fw={700} c={compliancePercent >= 95 ? 'green' : compliancePercent >= 80 ? 'yellow' : 'red'}>
              {compliancePercent}%
            </Text>
          </Stack>
          <RingProgress
            size={60}
            thickness={6}
            roundCaps
            sections={[
              { value: compliancePercent, color: compliancePercent >= 95 ? 'green' : compliancePercent >= 80 ? 'yellow' : 'red' },
            ]}
          />
        </Group>
      </Card>
    );
  }

  return (
    <Card withBorder padding="lg">
      <Group justify="space-between" mb="md">
        <Text fw={600}>{title}</Text>
        <Tooltip label="21st Century Cures Act requires GPS verification">
          <ThemeIcon size="sm" variant="light" color="blue">
            <IconMapPin size={14} />
          </ThemeIcon>
        </Tooltip>
      </Group>

      <Group justify="space-between" align="flex-start" mb="md">
        <Stack gap="xs">
          <Group gap="xs">
            <ThemeIcon size="sm" variant="light" color="green">
              <IconCheck size={14} />
            </ThemeIcon>
            <Text size="sm">{withGps} with GPS</Text>
          </Group>
          <Group gap="xs">
            <ThemeIcon size="sm" variant="light" color="red">
              <IconMapPinOff size={14} />
            </ThemeIcon>
            <Text size="sm">{withoutGps} without GPS</Text>
          </Group>
          {withoutGps > 0 && (
            <Group gap="xs">
              <ThemeIcon size="sm" variant="light" color="orange">
                <IconAlertTriangle size={14} />
              </ThemeIcon>
              <Text size="xs" c="orange">Requires exception documentation</Text>
            </Group>
          )}
        </Stack>

        <RingProgress
          size={80}
          thickness={8}
          roundCaps
          label={
            <Text size="sm" ta="center" fw={700}>
              {compliancePercent}%
            </Text>
          }
          sections={[
            { value: compliancePercent, color: compliancePercent >= 95 ? 'green' : compliancePercent >= 80 ? 'yellow' : 'red' },
          ]}
        />
      </Group>

      <Stack gap="xs">
        <Text size="xs" c="dimmed">GPS Accuracy Distribution</Text>
        <SimpleGrid cols={3}>
          <Tooltip label="Within 10 meters">
            <Stack gap={2}>
              <Text size="xs" c="dimmed">High</Text>
              <Text size="sm" fw={600} c="green">{highAccuracy}</Text>
            </Stack>
          </Tooltip>
          <Tooltip label="10-50 meters">
            <Stack gap={2}>
              <Text size="xs" c="dimmed">Medium</Text>
              <Text size="sm" fw={600} c="yellow">{mediumAccuracy}</Text>
            </Stack>
          </Tooltip>
          <Tooltip label="Over 50 meters">
            <Stack gap={2}>
              <Text size="xs" c="dimmed">Low</Text>
              <Text size="sm" fw={600} c="orange">{lowAccuracy}</Text>
            </Stack>
          </Tooltip>
        </SimpleGrid>
      </Stack>
    </Card>
  );
}

export default EvvSummaryWidget;
